/**
 * "Screen a candidate" — run Cora on one named candidate now, outside the hourly run.
 *
 * Paste a Lever link or ID. The position comes from the candidate's posting; pick another Cora
 * position to score them against a different opening. In live mode the note and tags go to Lever
 * (a second note only after confirming); otherwise it is a preview that writes nothing.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { CoraApiError, fetchCoraPositions, screenCoraCandidate, type CoraMode } from '../../services/coraApi';
import { useCoraWrite } from './useCoraWrite';

const FROM_POSTING = 'from-posting';

export default function ScreenCandidateForm({ mode, onScreened }: { mode: CoraMode; onScreened: (runId: string) => void }) {
  const queryClient = useQueryClient();
  const [candidate, setCandidate] = useState('');
  const [positionId, setPositionId] = useState(FROM_POSTING);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const positions = useQuery({ queryKey: ['cora', 'positions'], queryFn: fetchCoraPositions, staleTime: 5 * 60_000 });
  const live = mode === 'live';
  // In live mode screening writes to Lever, which needs Cora Operator access (the proxy enforces it too).
  const canWrite = useCoraWrite();
  const blocked = live && !canWrite;

  const screenNow = async (confirmDuplicateNote: boolean) => {
    setBusy(true);
    const chosen = positionId === FROM_POSTING ? undefined : positionId;
    void logAnalyticsEvent('cora_manual_screen_clicked', { live, chosen_position: Boolean(chosen), second_note: confirmDuplicateNote });
    try {
      const result = await screenCoraCandidate(candidate, mode, chosen, confirmDuplicateNote);
      void logAnalyticsEvent('cora_manual_screen_succeeded', { status: result.status, mode: result.mode });
      showToast({
        status: 'success',
        description: result.mode === 'live' ? 'Cora screened the candidate and wrote her note to Lever.' : 'Cora screened the candidate (preview — nothing written to Lever).',
      });
      setConfirming(false);
      setCandidate('');
      void queryClient.invalidateQueries({ queryKey: ['cora', 'runs'] });
      onScreened(result.id);
    } catch (err) {
      if (err instanceof CoraApiError && err.code === 'needs-confirmation') setConfirming(true);
      else if (err instanceof CoraApiError && err.code === 'mode-changed') {
        // Reload the mode so the page says what will actually happen, then let the person decide again.
        setConfirming(false);
        void queryClient.invalidateQueries({ queryKey: ['cora', 'status'] });
        showToast({ status: 'info', description: err.message });
      } else {
        void logAnalyticsEvent('cora_manual_screen_failed');
        showToast({ status: 'error', description: err instanceof Error ? err.message : 'Cora could not screen this candidate.' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-sm border border-solid border-pearl p-4">
      <div>
        <h2 className="text-sm font-semibold text-zinc-900">Screen a candidate</h2>
        <p className="text-xs text-zinc-600">
          {live
            ? 'Cora screens the candidate now and writes her note and tags to Lever.'
            : 'Preview: Cora screens the candidate now, but nothing is written to Lever.'}{' '}
          Takes about a minute.
        </p>
        {blocked && (
          <p className="mt-1 text-xs text-orange">
            Screening in live mode needs Cora Operator access. Ask to be added to the Cora operators group.
          </p>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-[1fr_18rem_auto] md:items-end">
        <Input
          label="Lever candidate link or ID"
          value={candidate}
          onChange={(e) => {
            setCandidate((e.target as HTMLInputElement).value);
            setConfirming(false);
          }}
        />
        <Select value={positionId} onValueChange={(v: string) => { setPositionId(v); setConfirming(false); }}>
          <SelectTrigger aria-label="Position to score against"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={FROM_POSTING}>Position of their Lever posting</SelectItem>
            {(positions.data?.positions ?? []).map((p) => (
              <SelectItem key={p.positionId} value={p.positionId}>{p.title} (#{p.positionId})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="primary" onClick={() => screenNow(false)} disabled={busy || blocked || !candidate.trim()}>
          {busy ? 'Cora is reading…' : 'Screen with Cora'}
        </Button>
      </div>
      {confirming && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-orange">
          This candidate already has a Cora note in Lever. Screening again posts a second note and replaces her verdict tag.
          <Button variant="primary" onClick={() => screenNow(true)} disabled={busy}>Post second note</Button>
          <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
        </div>
      )}
    </section>
  );
}
